import { useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import gql from "graphql-tag";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Zoom } from "../../../App";
import { ConnectionIndicator } from "./Components/ConnectionIndicator";
import { OrderDetail } from "./OrderDetail/OrderDetail";
import { MonitorOrderSummary, OrderList } from "./OrderList/OrderList";
import "./OrderMonitor.css";
import { BulkActions } from "./OrderRemediation/BulkActions";

const MONITOR_ORDERS_QUERY = gql`
  query MonitorOrders($filter: MonitorOrdersFilter) {
    monitorOrders(filter: $filter) {
      orders {
        id
        accountId
        accountName
        orderLines {
          amount
          productNumber
          manuallyAdded
          name
          images {
            url
            name
            sorting
          }
          prodNo
          manualAdd
          isMalformed
          missingFields
          status
          replacementProducts
        }
        automaticOrderDate
        status
        effectiveStatus
        errorMessage
        errorDetails
        comment
        createdAt
        updatedAt
        lastOrderedAt
        orderNumber
        orderDate
        orderChannel
        deliveryId
        isBackorder
        isCancelled
        itemCount
        malformedItemCount
        duplicateOrderNumber
      }
      totalCount
      page
      pageSize
    }
  }
`;

const MONITOR_ORDER_DETAIL_QUERY = gql`
  query MonitorOrderDetail($orderId: String!) {
    monitorOrderDetail(orderId: $orderId) {
      order {
        id
        accountId
        accountName
        orderLines {
          amount
          productNumber
          manuallyAdded
          name
          images {
            url
            name
            sorting
          }
          prodNo
          manualAdd
          isMalformed
          missingFields
          status
          replacementProducts
        }
        automaticOrderDate
        status
        effectiveStatus
        errorMessage
        errorDetails
        comment
        createdAt
        updatedAt
        lastOrderedAt
        orderNumber
        orderDate
        orderChannel
        deliveryId
        isBackorder
        isCancelled
        itemCount
        malformedItemCount
        duplicateOrderNumber
      }
      auditTrail {
        id
        actionType
        performedBy
        performedAt
        details
      }
    }
  }
`;

const RESEND_ORDER_MUTATION = gql`
  mutation ResendOrder($orderId: String!) {
    resendOrder(orderId: $orderId) {
      success
      orderNumber
      error
    }
  }
`;

const DISMISS_ORDER_MUTATION = gql`
  mutation DismissOrder($orderId: String!, $reason: String) {
    dismissOrder(orderId: $orderId, reason: $reason) {
      id
      status
      effectiveStatus
    }
  }
`;

const AUTO_FIX_ORDER_MUTATION = gql`
  mutation AutoFixOrder($orderId: String!) {
    autoFixOrder(orderId: $orderId) {
      id
      accountId
      accountName
      orderLines {
        amount
        productNumber
        manuallyAdded
        name
        images {
          url
          name
          sorting
        }
        prodNo
        manualAdd
        isMalformed
        missingFields
        status
        replacementProducts
      }
      automaticOrderDate
      status
      effectiveStatus
      errorMessage
      errorDetails
      comment
      createdAt
      updatedAt
      lastOrderedAt
      orderNumber
      orderDate
      orderChannel
      deliveryId
      isBackorder
      isCancelled
      itemCount
      malformedItemCount
    }
  }
`;

const ON_MONITOR_ORDER_UPDATED_SUBSCRIPTION = gql`
  subscription OnMonitorOrderUpdated {
    onMonitorOrderUpdated {
      id
      accountId
      accountName
      orderLines {
        amount
        productNumber
        manuallyAdded
        name
        images {
          url
          name
          sorting
        }
        prodNo
        manualAdd
        isMalformed
        missingFields
        status
        replacementProducts
      }
      automaticOrderDate
      status
      effectiveStatus
      errorMessage
      errorDetails
      comment
      createdAt
      updatedAt
      lastOrderedAt
      orderNumber
      orderDate
      orderChannel
      deliveryId
      isBackorder
      isCancelled
      itemCount
      malformedItemCount
      duplicateOrderNumber
    }
  }
`;

type MonitorOrdersFilter = {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

const POLL_INTERVAL = 30000; // 30 seconds fallback polling
const TOAST_AUTO_CLOSE = 10000; // 10 seconds auto-dismiss

export const OrderMonitor = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [filter, setFilter] = useState<MonitorOrdersFilter>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [wsConnected, setWsConnected] = useState(true);
  const [resending, setResending] = useState(false);

  // Track known order IDs to detect new failed orders
  const knownOrderIdsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  // Main orders list query
  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
    startPolling,
    stopPolling,
  } = useQuery(MONITOR_ORDERS_QUERY, {
    variables: { filter },
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      // Track known order IDs on initial load
      if (initialLoadRef.current && data?.monitorOrders?.orders) {
        const ids = data.monitorOrders.orders.map((o: { id: string }) => o.id);
        knownOrderIdsRef.current = new Set(ids);
        initialLoadRef.current = false;
      }
    },
  });

  // Order detail lazy query
  const [
    fetchOrderDetail,
    { data: detailData, loading: detailLoading },
  ] = useLazyQuery(MONITOR_ORDER_DETAIL_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  // Resend mutation
  const [resendOrder] = useMutation(RESEND_ORDER_MUTATION);

  // Auto-fix mutation
  const [autoFixOrder] = useMutation(AUTO_FIX_ORDER_MUTATION);

  // Dismiss mutation
  const [dismissOrderMutation] = useMutation(DISMISS_ORDER_MUTATION);

  // Real-time subscription
  useSubscription(ON_MONITOR_ORDER_UPDATED_SUBSCRIPTION, {
    onSubscriptionData: ({ subscriptionData }) => {
      const updatedOrder = subscriptionData.data?.onMonitorOrderUpdated;
      if (!updatedOrder) return;

      // Mark websocket as connected when we receive data
      if (!wsConnected) {
        setWsConnected(true);
        stopPolling();
      }

      // Show toast for new failed orders
      if (
        updatedOrder.status === "failed" &&
        !knownOrderIdsRef.current.has(updatedOrder.id)
      ) {
        toast(
          `Ny fejlet ordre: ${updatedOrder.accountName} (${updatedOrder.accountId})`,
          {
            className: "u-toast-error",
            progressClassName: "u-toast-error-bar",
            transition: Zoom,
            autoClose: TOAST_AUTO_CLOSE,
          }
        );
      }

      // Add to known IDs
      knownOrderIdsRef.current.add(updatedOrder.id);

      // Refetch orders list to get updated data
      refetchOrders();

      // Refetch detail if the updated order is currently selected
      if (selectedOrderId === updatedOrder.id) {
        fetchOrderDetail({ variables: { orderId: updatedOrder.id } });
      }
    },
    onError: () => {
      // Websocket disconnected — enable fallback polling
      setWsConnected(false);
      startPolling(POLL_INTERVAL);
    },
  });

  // Handle websocket reconnection detection
  useEffect(() => {
    if (!wsConnected) {
      startPolling(POLL_INTERVAL);
    } else {
      stopPolling();
    }
  }, [wsConnected, startPolling, stopPolling]);

  // Select an order and fetch its detail
  const handleSelectOrder = useCallback(
    (orderId: string) => {
      setSelectedOrderId(orderId);
      fetchOrderDetail({ variables: { orderId } });
    },
    [fetchOrderDetail]
  );

  // Filter change handler
  const handleFilterChange = useCallback(
    (newFilter: { status?: string; search?: string; page?: number }) => {
      setFilter((prev) => ({ ...prev, ...newFilter }));
    },
    []
  );

  // Retry handler for error state
  const handleRetry = useCallback(() => {
    refetchOrders();
  }, [refetchOrders]);

  // Auto-fix handler
  const handleAutoFix = useCallback(() => {
    if (!selectedOrderId) return;

    autoFixOrder({
      variables: { orderId: selectedOrderId },
      onCompleted: () => {
        fetchOrderDetail({ variables: { orderId: selectedOrderId } });
        refetchOrders();
        toast("Auto-rettelse gennemført", {
          className: "u-toast-success",
          progressClassName: "u-toast-success-bar",
          transition: Zoom,
        });
      },
      onError: (error: { message: string }) => {
        toast(error.message || "Fejl ved auto-rettelse", {
          className: "u-toast-error",
          progressClassName: "u-toast-error-bar",
          transition: Zoom,
        });
      },
    });
  }, [selectedOrderId, autoFixOrder, fetchOrderDetail, refetchOrders]);

  // Resend handler
  const handleResend = useCallback(() => {
    if (!selectedOrderId) return;

    setResending(true);
    resendOrder({
      variables: { orderId: selectedOrderId },
      onCompleted: (data: any) => {
        setResending(false);
        const result = data.resendOrder;

        if (result.success) {
          toast(
            result.orderNumber
              ? `Ordre ${result.orderNumber} gensendt`
              : "Ordre gensendt",
            {
              className: "u-toast-success",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            }
          );
        } else {
          toast(result.error || "Fejl ved gensendelse af ordre", {
            className: "u-toast-error",
            progressClassName: "u-toast-error-bar",
            transition: Zoom,
          });
        }

        // Refresh data
        fetchOrderDetail({ variables: { orderId: selectedOrderId } });
        refetchOrders();
      },
      onError: (error: { message: string }) => {
        setResending(false);
        toast(error.message || "Fejl ved gensendelse af ordre", {
          className: "u-toast-error",
          progressClassName: "u-toast-error-bar",
          transition: Zoom,
        });
      },
    });
  }, [selectedOrderId, resendOrder, fetchOrderDetail, refetchOrders]);

  // Dismiss handler
  const handleDismiss = useCallback(() => {
    if (!selectedOrderId) return;

    dismissOrderMutation({
      variables: { orderId: selectedOrderId },
      onCompleted: () => {
        toast("Ordre afvist", {
          className: "u-toast-success",
          progressClassName: "u-toast-success-bar",
          transition: Zoom,
        });
        fetchOrderDetail({ variables: { orderId: selectedOrderId } });
        refetchOrders();
      },
      onError: (error: { message: string }) => {
        toast(error.message || "Fejl ved afvisning af ordre", {
          className: "u-toast-error",
          progressClassName: "u-toast-error-bar",
          transition: Zoom,
        });
      },
    });
  }, [selectedOrderId, dismissOrderMutation, fetchOrderDetail, refetchOrders]);

  // Bulk auto-fix complete handler
  const handleBulkAutoFixComplete = useCallback(() => {
    refetchOrders();
    setSelectedIds([]);
    if (selectedOrderId) {
      fetchOrderDetail({ variables: { orderId: selectedOrderId } });
    }
  }, [refetchOrders, selectedOrderId, fetchOrderDetail]);

  // Extract data
  const orders: MonitorOrderSummary[] = ordersData?.monitorOrders?.orders ?? [];
  const totalCount = ordersData?.monitorOrders?.totalCount ?? 0;
  const page = ordersData?.monitorOrders?.page ?? 1;
  const pageSize = ordersData?.monitorOrders?.pageSize ?? 50;

  const orderDetail = detailData?.monitorOrderDetail?.order ?? null;
  const auditTrail = detailData?.monitorOrderDetail?.auditTrail ?? [];

  return (
    <div className="OrderMonitor">
      <ConnectionIndicator connected={wsConnected} />

      <div className="OrderMonitor__content">
        <div className="OrderMonitor__list-panel">
          {selectedIds.length > 0 && (
            <BulkActions
              selectedIds={selectedIds}
              onBulkAutoFix={handleBulkAutoFixComplete}
            />
          )}
          <OrderList
            orders={orders}
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            loading={ordersLoading}
            error={ordersError?.message ?? null}
            selectedId={selectedOrderId}
            onSelect={handleSelectOrder}
            onFilterChange={handleFilterChange}
            onRetry={handleRetry}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </div>

        {selectedOrderId && orderDetail && (
          <div className="OrderMonitor__detail-panel">
            {detailLoading && (
              <div className="OrderMonitor__detail-loading">
                Indlæser ordredetaljer...
              </div>
            )}
            <OrderDetail
              order={orderDetail}
              auditTrail={auditTrail}
              onAutoFix={handleAutoFix}
              onResend={handleResend}
              onDismiss={handleDismiss}
              onClose={() => setSelectedOrderId(null)}
              resending={resending}
            />
          </div>
        )}
      </div>
    </div>
  );
};
